import { compareVersions } from 'compare-versions'
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, AppState, AppStateStatus, BackHandler, ImageBackground, Linking, Platform } from 'react-native'
import { checkVersion } from 'react-native-check-version'

import { EnvironmentStages, useStore } from '@/models'
import { extendLogger, LoggerContexts } from '../logger'
import { getAppVersion } from '../appVersion'
import { useRemoteConfig } from '../../../module/remote-config'
import { segmentClient } from '@/models/analytics/segment'

const logger = extendLogger(LoggerContexts.AppVersionAlert)

const STAGE = process.env.EXPO_PUBLIC_STAGE
const isProd = STAGE === EnvironmentStages.prod

const defaultAppVersionState = { isLoading: true, needsUpdate: false, url: '' }

const AppVersionContext = createContext(defaultAppVersionState)

export const AppVersionContextProvider = (props: { children: React.ReactNode }) => {
  const { children } = props
  const [update, setUpdate] = useState(defaultAppVersionState)
  const appState = useRef(AppState.currentState)
  const isChecking = useRef(false)
  const {
    analytics: { setEvent = undefined } = { setEvent: undefined }
  } = useStore()
  const { versionForTesting } = useRemoteConfig((state) => state.test_json)

  const checkAppStores = useCallback(async (testVersion: string, sendEvent: typeof setEvent) => {
    // Prevent concurrent checks
    if (isChecking.current) return

    logger.debug('checkAppStores', 'start checking...')
    isChecking.current = true

    // If in dev mode/stage/etc, and `testVersion` has a value then use value as if from AppStore
    try {
      // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
      if (!isProd && !testVersion) {
        // assume we are good if apple or google servers are down
        setUpdate((prevUpdate) => ({ ...prevUpdate, isLoading: false }))
        logger.debug('checkAppStores', 'Not in prod or test, will not check for updates')
      } else {
        // Installed binary version, not the OTA bundle's — see utils/appVersion.
        const currentVersion = getAppVersion()
        const updateNeeded = await checkVersion()

        // https://github.com/omichelsen/compare-versions#usage
        // compareVersions('11.1.1', '10.0.0') //  1
        // compareVersions('10.0.0', '10.0.0') //  0
        // compareVersions('10.0.0', '11.1.1') // -1
        const latestVersion = testVersion || updateNeeded?.version
        const needsUpdate = compareVersions(currentVersion, latestVersion) === -1
        if (needsUpdate) {
          sendEvent?.('App Upgrade Required', {
            olderVersion: currentVersion,
            newerVersion: latestVersion
          })
          logger.info('checkAppStores', `Update required from ${currentVersion} to ${latestVersion}`)
        } else {
          logger.debug('checkAppStores', `Update NOT required to ${latestVersion}`)
        }

        setUpdate({ isLoading: false, needsUpdate, url: updateNeeded.url })
      }
    } catch (error) {
      // assume we are good if apple or google servers are down
      setUpdate((prevUpdate) => ({ ...prevUpdate, isLoading: false }))
      logger.error('checkAppStores', 'Error when checking the app version', error)
    } finally {
      isChecking.current = false
      logger.debug('checkAppStores', 'done checking')
    }
  }, [])

  useEffect(() => {
    checkAppStores(versionForTesting, setEvent)
  }, [checkAppStores, versionForTesting, setEvent])

  // Check for app store updates when app comes to foreground
  useEffect(() => {
    logger.debug('useEffect', `first-time 'isProd' is ${isProd}`)
    if (!isProd) return

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      logger.debug('useEffect', `current ${appState.current} nextAppState ${nextAppState}`)
      // App has come to the foreground from background
      if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
        logger.debug('useEffect', 'foregrounded, call checkAppStores()')
        checkAppStores(versionForTesting, setEvent)
      }

      // if (appState.current.match(/active/) && nextAppState === 'inactive') {
      //   logger.info('useEffect', 'app going inactive')
      // }

      // if (appState.current.match(/active/) && nextAppState === 'background') {
      //   logger.debug('useEffect', 'app going to background')
      // }
      appState.current = nextAppState
    }

    logger.debug('useEffect', 'Setting up app listener to check version')
    const subscription = AppState.addEventListener('change', handleAppStateChange)

    return () => {
      subscription.remove()
    }
  }, [checkAppStores, versionForTesting, setEvent])

  if (update.isLoading) {
    logger.debug('AppVersionContextProvider', 'isLoading')
    return <ImageBackground style={{ flex: 1 }} source={require('../../../assets/splashscreen_image.png')} />
  }

  return <AppVersionContext.Provider value={update}>{children}</AppVersionContext.Provider>
}

export const useAppVersion = () => {
  const {
    analytics: { setEvent = undefined } = { setEvent: undefined }
  } = useStore()
  const update = React.useContext(AppVersionContext)

  const showUpdateAlert = useCallback(
    (isExternal = false) => {
      logger.info(`showUpdateAlert: displaying alert, isExternal is ${isExternal}`)
      // Alert was 'Update version', 'Please update to the latest version'. My edits below.
      Alert.alert('Newer Version Available', 'Please update your version of the app to the latest', [
        {
          text: 'Update',
          onPress: async () => {
            setEvent?.('App Upgrade Lock Cleared')
            await segmentClient.flush()
            logger.info('showUpdateAlert: update notice viewed and cleared')
            // TODO how to flush logger before app quit by new installation?
            // FYI Nothing in Datadog suggests we can do this

            // TODO: kill the app, so user can open again with new version updated
            // BackHandler.exitApp() is Android only! https://reactnative.dev/docs/backhandler
            // iOS relies on AppStore killing app when user clicks "Open".
            // But app would still be running and stale if users doesn't wait and then 'Open'
            if (Platform.OS === 'android') BackHandler.exitApp()

            // I find this strange, causes double popups on iOS at minimum, sometimes endless loops - PEK
            if (update.url !== '') {
              logger.info(`showUpdateAlert: has URL, using Linking to jump to ${update.url}`)
              // TODO again, how to flush logger before app quit by new installation?

              await Linking.openURL(update.url)
            }
          }
        }
      ])
    },
    [update.url, setEvent]
  )

  return {
    needsUpdate: update.needsUpdate,
    showUpdateAlert
  }
}
